// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useEffect, useRef, useState } from 'react';
// eslint-disable-next-line import/no-unresolved
import SVG from 'react-inlinesvg';
import styles from './labelstyle';
import copyIcon from './images/assets/copy-icon.svg';
import editIcon from './images/assets/text-input-icon.svg';
import deleteIcon from './images/assets/trash-icon.svg';
import pencilIcon from './images/assets/FillPencilSimpleLine.svg';
import CloseIcon from './images/assets/closeAnnotation.svg';

interface LabelProps {
  view: __esri.MapView;
  mapPoint: __esri.Point;
  color: string;
  text: string;
  onDelete: () => void;
  onEdit: (newText: string) => void;
  onColorChange: (newColor: string) => void;
  viewWidth: any
}

const LabelComponent: React.FC<LabelProps> = ({
  view,
  mapPoint,
  color,
  text,
  onDelete,
  onEdit,
  onColorChange,
  viewWidth,

}) => {
  const labelRef = useRef<HTMLDivElement>(null);

  const [isEditing, setIsEditing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showPalette, setShowPalette] = useState(false);

  const [labelText, setLabelText] = useState(text);
  const [dynColors, setdynColors] = useState<string | null>(null);
  const [labelWidth, setlabelWidth] = useState(null);
  const tempWidth = view.width;
  const colors = [
    '#01A1DD', '#E91EAF',
    '#2962FF', '#5BD5D8',
    '#FFA726', '#F44336',
  ];

  // Keep label text updated when parent updates 'text'
  useEffect(() => setLabelText(text), [text]);

  // Keep label positioned with map movement
  useEffect(() => {
    if (!view || !mapPoint || !labelRef.current) return;

    const updatePosition = () => {
      const screenPoint = view.toScreen(mapPoint);
      if (screenPoint && labelRef.current) {
        labelRef.current.style.left = `${screenPoint.x + 2}px`;
        labelRef.current.style.top = `${screenPoint.y - 10}px`;
      }
    };

    updatePosition();
    const handlers = [
      view.watch('extent', updatePosition),
      view.watch('stationary', updatePosition),
      view.watch('rotation', updatePosition),
    ];

    return () => handlers.forEach((h) => h.remove());
  }, [view, mapPoint]);

  const appliedColor = dynColors || color;

  return (
    <div ref={labelRef} style={styles.labeldiv(viewWidth)}>

      {/* TOP TOOLBAR */}
      {isExpanded && (
        <div style={styles.labelEditDiv}>

          {/* CURRENT COLOR BOX */}
          <div
            style={styles.ColorDiv(appliedColor)}
            onClick={() => setShowPalette(!showPalette)}
          />

          {/* COLOR PALETTE */}
          {showPalette && (
            <div style={styles.paletteContainer}>
              {colors.map((c) => (
                <div
                  key={c}
                  style={styles.paletteColor(c)}
                  onClick={() => {
                    setdynColors(c);
                    onColorChange(c);
                    setShowPalette(false);
                  }}
                />
              ))}
            </div>
          )}

          {/* ICONS */}
          <div style={styles.IconDiv}>
            <SVG
              src={editIcon}
              onClick={() => {
                setIsEditing(true);
                setIsExpanded(false);
              }}
              style={styles.labelIcons}
              title="Edit"
            />

            <SVG
              src={copyIcon}
              onClick={() => navigator.clipboard.writeText(labelText)}
              style={styles.labelIcons}
              title="Copy"
            />

            <SVG
              src={deleteIcon}
              onClick={onDelete}
              style={styles.labelIcons}
              title="Delete"
            />
          </div>
        </div>
      )}

      {/* LABEL ROW */}
      <div style={styles.labelRow}>
        {isEditing ? (
          <input
            autoFocus
            value={labelText}
            onChange={(e) => setLabelText(e.target.value)}
            onBlur={() => {
              setIsEditing(false);
              //  Call onEdit ONLY if text is valid
              if (labelText && labelText.trim() !== '' && labelText !== 'My Label') {
                onEdit(labelText);
              } else {
                //  revert UI to previous value
                setLabelText(text);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setIsEditing(false);

                if (labelText && labelText.trim() !== '') {
                  onEdit(labelText);
                } else {
                  setLabelText(text);
                }
              }
            }}
            style={{
              ...styles.labelContainer,
              backgroundColor: appliedColor,
            }}
          />
        ) : (
          <div
            style={{
              ...styles.labelTextSpan,
              backgroundColor: appliedColor,
            }}
          >
            <span>{labelText}</span>
          </div>
        )}

        {!isExpanded && (
          <SVG
            src={pencilIcon}
            onClick={() => setIsExpanded(true)}
            style={styles.EditPencil}
            title="Show Options"
          />
        )}
        {isExpanded && (
          <SVG
            src={CloseIcon}
            onClick={() => setIsExpanded(false)}
            style={styles.CloseIcon}
            title="Hide Options"
          />
        )}
      </div>

      {/* BOTTOM MARKER */}
      <div style={styles.labelMarker(color)} />
    </div>
  );
};

export default LabelComponent;
